import { useState, useEffect, useCallback, useRef } from 'react';
import { createLogger } from '@/lib/logger';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  RefreshControl,
  ActivityIndicator,
  Platform,
  Modal,
  ScrollView,
 Image, Alert, Pressable } from 'react-native';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { MessageCircle, Trophy, Plus, MapPin, Activity, Swords } from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { VideoView, useVideoPlayer } from 'expo-video';

import CreatePostModal from '@/components/CreatePostModal';
import DailyMotivation from '@/components/DailyMotivation';
import EmptyState from '@/components/EmptyState';
import FriendStreakWidget from '@/components/FriendStreakWidget';
import GymStreakWidget from '@/components/GymStreakWidget';
import UserIdentity from '@/components/UserIdentity';
import CommentsModal from '@/components/CommentsModal';
import { useRouter, useFocusEffect, useLocalSearchParams } from 'expo-router';
import * as Haptics from 'expo-haptics';
import { draftStore } from '@/lib/draftStore';
import { FLAGS } from '@/lib/featureFlags';

const log = createLogger('FEED');

interface FeedPost {
  id: string;
  user_id: string;
  caption: string | null;
  content: string | null;
  post_type: string;
  media_url: string | null;
  media_type: string | null;
  thumbnail_url: string | null;
  visibility: string;
  is_competition: boolean;
  is_verification_post: boolean;
  verification_status: 'open' | 'valid' | 'no_lift' | 'tie' | 'insufficient_votes';
  verification_closed_at: string | null;
  verification_message: string | null;
  like_count: number;
  comment_count: number;
  created_at: string;
  gym_id: string | null;
  profiles: {
    username: string;
    display_name: string;
    avatar_url: string | null;
  };
  gyms?: {
    name: string;
  } | null;
  user_liked?: boolean;
  verification_votes?: {
    good_count: number;
    bad_count: number;
    user_vote: 'good' | 'bad' | null;
    user_reason?: string | null;
    reason_breakdown?: { reason: string; count: number }[];
  };
}

type FeedFilter = 'all' | 'friends' | 'my_gym';

function VideoPlayer({ uri, isActive }: { uri: string; isActive?: boolean }) {
  const player = useVideoPlayer(uri || '', (p: any) => {
    p.loop = true;
  });

  useEffect(() => {
    if (!player || !uri) return;
    if (isActive) {
      try {
        player.play();
      } catch {}
    } else {
      try {
        player.pause();
      } catch {}
    }

    return () => {
      try {
        player.pause();
      } catch {}
    };
  }, [isActive, player, uri]);

  if (!uri) return null;

  return <VideoView player={player} style={styles.video} nativeControls contentFit="cover" />;
}

export default function FeedTab() {
  const { profile } = useAuth();
  const { theme } = useTheme();
  const router = useRouter();
  const params = useLocalSearchParams();
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState<FeedFilter>('all');
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [commentPostId, setCommentPostId] = useState<string | null>(null);
  const [commentPostAuthor, setCommentPostAuthor] = useState<string | undefined>(undefined);
  const [videoUri, setVideoUri] = useState<string | null>(null);
  const [postDraftId, setPostDraftId] = useState<string | null>(null);
  const [draftContent, setDraftContent] = useState<string>('');
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [pendingVote, setPendingVote] = useState<{ postId: string; vote: 'bad' } | null>(null);
  const [activeVideoKey, setActiveVideoKey] = useState<string | null>(null);

  useEffect(() => {
    loadFeed();

    // Subscribe to real-time posts
    const subscription = supabase
      .channel('posts_channel')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'posts' },
        () => {
          loadFeed();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [filter]);

  useFocusEffect(
    useCallback(() => {
      if (params.videoUri && params.contextId && params.source === 'feed') {
        const uri = params.videoUri as string;
        const contextId = params.contextId as string;

        const draft = draftStore.getFeedDraft(contextId) || { content: '', mediaUri: null, mediaType: null };

        setVideoUri(uri);
        setPostDraftId(contextId);
        setDraftContent(draft.content);
        setShowCreatePost(true);

        draftStore.clearFeedDraft(contextId);

        router.push({ pathname: '/(tabs)/feed' });
      }
    }, [params.videoUri, params.contextId, params.source])
  );

  // V1: Social feed disabled — redirect to home
  useEffect(() => {
    if (!FLAGS.V1_SOCIAL_FEED_ENABLED) {
      router.replace('/(tabs)/home');
    }
  }, [router]);

  const loadFeed = async () => {
    try {
      log.info('Loading feed with filter:', filter);

      let query = supabase
        .from('posts')
        .select(`
          *,
          profiles!posts_user_id_fkey (username, display_name, avatar_url),
          gyms!posts_gym_id_fkey (name)
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      if (filter === 'my_gym' && profile?.home_gym_id) {
        query = query.eq('gym_id', profile.home_gym_id);
      } else if (filter === 'friends' && profile?.id) {
        const { data: followingData } = await supabase
          .from('follows')
          .select('following_id')
          .eq('follower_id', profile.id);
        const followingIds = (followingData || []).map((f: any) => f.following_id).filter(Boolean);
        if (followingIds.length > 0) {
          query = query.in('user_id', followingIds);
        } else {
          // No following — return empty
          setPosts([]);
          setLoading(false);
          return;
        }
      }

      const { data, error } = await query;

      if (error) {
        log.error('Error loading feed:', error);
        throw error;
      }

      log.info(`Loaded ${data?.length || 0} posts from feed`);

      if (data && profile) {
        // Finalize verification posts that are past 48 hours and still open
        const now = new Date();
        const postsToFinalize = data.filter((post: any) => {
          if (!post.is_verification_post || post.verification_status !== 'open') {
            return false;
          }
          const createdAt = new Date(post.created_at);
          const hoursSinceCreation = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);
          return hoursSinceCreation >= 48;
        });

        // Call finalize function for each post that needs it
        await Promise.all(
          postsToFinalize.map(async (post: any) => {
            try {
              await supabase.rpc('finalize_verification_post', { target_post_id: post.id });
            } catch (error) {
              log.error('Error finalizing verification post:', error);
            }
          })
        );

        // Batch: fetch all likes for current user across all post IDs in one query
        const postIds = data.map((p: any) => p.id);
        const { data: userLikes } = await supabase
          .from('likes')
          .select('post_id')
          .eq('user_id', profile.id)
          .in('post_id', postIds);
        const likedPostIds = new Set((userLikes ?? []).map((l: any) => l.post_id));

        // Batch: fetch all verification votes for verification posts
        const verificationPostIds = data
          .filter((p: any) => p.is_verification_post && p.media_type && String(p.media_type).toLowerCase().includes('video'))
          .map((p: any) => p.id);

        let votesMap = new Map<string, any[]>();
        if (verificationPostIds.length > 0) {
          const { data: allVotes } = await supabase
            .from('post_verification_votes')
            .select('post_id, vote, voter_id, reason')
            .in('post_id', verificationPostIds);
          (allVotes ?? []).forEach((v: any) => {
            if (!votesMap.has(v.post_id)) votesMap.set(v.post_id, []);
            votesMap.get(v.post_id)!.push(v);
          });
        }

        const postsWithLikes = data.map((post: any) => {
          let verification_votes = null;
          const isVideo = post.media_type && String(post.media_type).toLowerCase().includes('video');

          if (post.is_verification_post && isVideo) {
            const votes = votesMap.get(post.id) || [];
            const good_count = votes.filter(v => v.vote === 'good').length;
            const bad_count = votes.filter(v => v.vote === 'bad').length;
            const userVote = votes.find(v => v.voter_id === profile.id);
            const user_vote = userVote?.vote || null;
            const user_reason = userVote?.reason || null;

            const badVotes = votes.filter(v => v.vote === 'bad' && v.reason);
            const reasonCounts = badVotes.reduce((acc: Record<string, number>, vote: any) => {
              acc[vote.reason] = (acc[vote.reason] || 0) + 1;
              return acc;
            }, {} as Record<string, number>);

            const reason_breakdown = Object.entries(reasonCounts)
              .map(([reason, count]) => ({ reason, count }))
              .sort((a, b) => b.count - a.count);

            verification_votes = { good_count, bad_count, user_vote, user_reason, reason_breakdown };
          }

          return { ...post, user_liked: likedPostIds.has(post.id), verification_votes };
        });
        setPosts(postsWithLikes as any);
      } else if (data) {
        setPosts(data as any);
      }
    } catch (error: any) {
      log.error('Failed to load feed:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const viewabilityConfig = { itemVisiblePercentThreshold: 50 };
  const onViewableItemsChanged = useRef(({ viewableItems }: { viewableItems: any[] }) => {
    const firstVisibleVideo = viewableItems.find((v) => {
      const mediaType = v.item?.media_type;
      return (
        mediaType && String(mediaType).toLowerCase().includes('video') && v.item?.media_url
      );
    });

    setActiveVideoKey(firstVisibleVideo ? firstVisibleVideo.item.media_url : null);
  });

  // V1: Social feed disabled — show nothing (redirect handled above)
  if (!FLAGS.V1_SOCIAL_FEED_ENABLED) {
    return null;
  }

  const handleRefresh = () => {
    setRefreshing(true);
    loadFeed();
  };

  const handleLike = async (postId: string, isLiked: boolean) => {
    if (Platform.OS !== 'web') {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }

    if (isLiked) {
      await supabase
        .from('likes')
        .delete()
        .eq('post_id', postId)
        .eq('user_id', profile!.id);

      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId
            ? { ...p, like_count: Math.max(0, p.like_count - 1), user_liked: false }
            : p,
        ),
      );
    } else {
      await supabase.from('likes').insert({
        post_id: postId,
        user_id: profile!.id,
        vote_type: 'flex',
      });

      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId ? { ...p, like_count: p.like_count + 1, user_liked: true } : p,
        ),
      );
    }
  };

  const handleVerificationVote = async (postId: string, vote: 'good' | 'bad') => {
    if (!profile) return;

    if (vote === 'bad') {
      setPendingVote({ postId, vote });
      setShowReasonModal(true);
      return;
    }

    try {
      const { error } = await supabase
        .from('post_verification_votes')
        .upsert(
          {
            post_id: postId,
            voter_id: profile.id,
            vote: vote,
            reason: null,
          },
          {
            onConflict: 'post_id,voter_id',
          }
        );

      if (error) throw error;

      await loadFeed();

      if (Platform.OS !== 'web') {
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      }
    } catch (error: any) {
      log.error('Error voting:', error);
    }
  };

  const submitBadVoteWithReason = async (reason: string) => {
    if (!profile || !pendingVote) return;

    try {
      const { error } = await supabase
        .from('post_verification_votes')
        .upsert(
          {
            post_id: pendingVote.postId,
            voter_id: profile.id,
            vote: 'bad',
            reason: reason,
          },
          {
            onConflict: 'post_id,voter_id',
          }
        );

      if (error) throw error;

      setShowReasonModal(false);
      setPendingVote(null);
      await loadFeed();

      if (Platform.OS !== 'web') {
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      }
    } catch (error: any) {
      log.error('Error voting:', error);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diff = now.getTime() - then.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return then.toLocaleDateString();
  };

  const isVotingOpen = (createdAt: string): boolean => {
    const now = new Date();
    const postDate = new Date(createdAt);
    const hoursSincePost = (now.getTime() - postDate.getTime()) / (1000 * 60 * 60);
    return hoursSincePost < 48;
  };

  const getVotingTimeRemaining = (createdAt: string): string => {
    const now = new Date();
    const postDate = new Date(createdAt);
    const hoursSincePost = (now.getTime() - postDate.getTime()) / (1000 * 60 * 60);
    const hoursRemaining = Math.max(0, 48 - hoursSincePost);

    if (hoursRemaining === 0) return 'Voting closed';
    if (hoursRemaining < 1) return `${Math.floor(hoursRemaining * 60)}m left`;
    if (hoursRemaining < 24) return `${Math.floor(hoursRemaining)}h left`;
    return `${Math.floor(hoursRemaining / 24)}d ${Math.floor(hoursRemaining % 24)}h left`;
  };

  const c = theme.colors;

  const renderPost = ({ item }: { item: FeedPost }) => {
    // Normalize media type to check for video
    const isVideo = item.media_type && String(item.media_type).toLowerCase().includes('video');
    const isAuthor = item.user_id === profile?.id;
    const votingOpen = isVotingOpen(item.created_at);
    const showVerification = item.is_verification_post && isVideo;

    return (
      <View style={[styles.postCard, { backgroundColor: c.surface, borderColor: c.border }]}>
        <View style={styles.postHeader}>
          <UserIdentity
            avatarUrl={item.profiles.avatar_url}
            displayName={item.profiles.display_name}
            username={item.profiles.username}
            size="small"
            showUsername={false}
            showBadge={true}
          />
          <View style={styles.postHeaderInfo}>
            <Text style={[styles.displayName, { color: c.text }]}>{item.profiles.display_name}</Text>
            <Text style={[styles.username, { color: c.textTertiary }]}>@{item.profiles.username}</Text>
            {item.gyms && (
              <View style={styles.gymInfo}>
                <MapPin size={12} color={c.textTertiary} />
                <Text style={[styles.gymName, { color: c.textTertiary }]}>{item.gyms.name}</Text>
              </View>
            )}
          </View>
          <View style={styles.postMeta}>
            <Text style={[styles.timestamp, { color: c.textTertiary }]}>{formatTimestamp(item.created_at)}</Text>
            {item.post_type === 'pr' && (
              <View style={[styles.prBadge, { backgroundColor: c.warning + '1A', borderColor: c.warning }]}>
                <Trophy size={14} color={c.warning} />
                <Text style={[styles.prBadgeText, { color: c.warning }]}>PR</Text>
              </View>
            )}
            {item.is_competition && (
              <View style={[styles.competitionBadge, { backgroundColor: c.error + '1A', borderColor: c.error }]}>
                <Text style={[styles.competitionBadgeText, { color: c.error }]}>COMP</Text>
              </View>
            )}
          </View>
        </View>

      {(item.caption || item.content) && (
        <Text style={[styles.postContent, { color: c.textSecondary }]}>{item.caption || item.content}</Text>
      )}

      {item.media_url && (
        <View style={[styles.mediaContainer, { backgroundColor: c.surfaceVariant }]}>
          {Platform.OS === 'web' || item.media_type === 'image' ? (
            <Image
              source={{ uri: item.media_url }}
              style={styles.image}
              resizeMode="cover"
            />
          ) : (
            <VideoPlayer
              key={item.media_url ?? 'video'}
              uri={item.media_url!}
              isActive={activeVideoKey === item.media_url}
            />
          )}
        </View>
      )}

      {showVerification && item.verification_votes && (
        <View style={[styles.verificationSection, { backgroundColor: c.background, borderColor: c.border }]}>
          {item.verification_status === 'open' ? (
            <>
              <View style={styles.verificationHeader}>
                <View>
                  <Text style={[styles.verificationLabel, { color: c.accent }]}>Valid lift?</Text>
                  <Text style={[styles.verificationTimeRemaining, { fontSize: 10, color: c.textTertiary }]}>
                    Verification affects trust level only; outcomes remain performance-based.
                  </Text>
                  <Text style={[styles.verificationTimeRemaining, { color: c.textTertiary }]}>
                    {getVotingTimeRemaining(item.created_at)}
                  </Text>
                </View>
                <View style={styles.verificationCounts}>
                  <Text style={[styles.verificationCount, { color: c.textSecondary }]}>⚪ {item.verification_votes.good_count}</Text>
                  <Text style={[styles.verificationCount, { color: c.textSecondary }]}>❌ {item.verification_votes.bad_count}</Text>
                </View>
              </View>
              {!isAuthor ? (
                votingOpen ? (
                  <View style={styles.verificationButtons}>
                    <TouchableOpacity
                      style={[
                        styles.verificationButton,
                        { backgroundColor: c.success + '1A', borderColor: c.success + '44' },
                        item.verification_votes.user_vote === 'good' && styles.verificationButtonActive,
                      ]}
                      onPress={() => handleVerificationVote(item.id, 'good')}>
                      <Text style={[styles.verificationButtonText, { color: c.text }]}>⚪ White Light</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[
                        styles.verificationButton,
                        { backgroundColor: c.error + '1A', borderColor: c.error + '44' },
                        item.verification_votes.user_vote === 'bad' && styles.verificationButtonActive,
                      ]}
                      onPress={() => handleVerificationVote(item.id, 'bad')}>
                      <Text style={[styles.verificationButtonText, { color: c.text }]}>❌ No Lift</Text>
                    </TouchableOpacity>
                  </View>
                ) : (
                  <Text style={[styles.votingClosedText, { color: c.textTertiary }]}>Voting window closed</Text>
                )
              ) : (
                <Text style={[styles.votingClosedText, { color: c.textTertiary }]}>Valid lift?</Text>
              )}
            </>
          ) : (
            <View style={styles.verificationVerdict}>
              <Text style={[styles.verdictMessage, { color: c.text }]}>{item.verification_message}</Text>
              <View style={styles.verdictCounts}>
                <Text style={[styles.verdictCount, { color: c.textSecondary }]}>⚪ {item.verification_votes.good_count}</Text>
                <Text style={[styles.verdictCount, { color: c.textSecondary }]}>❌ {item.verification_votes.bad_count}</Text>
              </View>
              {item.user_id === profile?.id &&
               item.verification_votes.reason_breakdown &&
               item.verification_votes.reason_breakdown.length > 0 && (
                <View style={[styles.reasonBreakdown, { borderTopColor: c.border }]}>
                  <Text style={[styles.reasonBreakdownTitle, { color: c.textSecondary }]}>
                    No Lift reasons:
                  </Text>
                  <Text style={[styles.reasonBreakdownText, { color: c.text }]}>
                    {item.verification_votes.reason_breakdown
                      .map(r => {
                        const label = {
                          DEPTH: 'Depth',
                          LOCKOUT: 'Lockout',
                          FORM_BREAKDOWN: 'Form',
                          RANGE_OF_MOTION: 'ROM',
                          PAUSE_STANDARD: 'Pause',
                          CONTROL: 'Control',
                          OTHER: 'Other'
                        }[r.reason] || r.reason;
                        return `${label} (${r.count})`;
                      })
                      .join(', ')}
                  </Text>
                </View>
              )}
            </View>
          )}
        </View>
      )}

      <View style={[styles.postActions, { borderTopColor: c.border }]}>
        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => handleLike(item.id, item.user_liked || false)}>
          <Text style={[styles.flexEmoji, item.user_liked && styles.flexEmojiActive]}>+1</Text>
          <Text style={[styles.actionText, { color: c.textSecondary }, item.user_liked && { color: c.accent }]}>
            {item.like_count}
          </Text>
        </TouchableOpacity>
        <Pressable
          style={styles.actionButton}
          onPress={() => {
            const author = (item as any).profiles?.display_name || (item as any).profiles?.handle;
            setCommentPostAuthor(author);
            setCommentPostId(item.id);
          }}
          accessibilityRole="button"
          accessibilityLabel={`${item.comment_count} comments`}
          android_ripple={{ color: 'rgba(255,255,255,0.1)', borderless: false }}
        >
          <MessageCircle size={20} color={c.textSecondary} />
          <Text style={[styles.actionText, { color: c.textSecondary }]}>{item.comment_count}</Text>
        </Pressable>
      </View>
    </View>
    );
  };

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color={c.accent} />
        <Text style={[styles.loadingText, { color: c.textSecondary }]}>Loading feed...</Text>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <LinearGradient
        colors={[theme.colors.background, theme.colors.surfaceVariant]}
        style={styles.header}>
        <View style={styles.logoContainer}>
          <Swords size={32} color={theme.colors.accent} strokeWidth={2.5} />
          <Text style={[styles.logoText, { color: theme.colors.accent }]}>DUEL</Text>
        </View>

        <View style={styles.filterContainer}>
          {(['all', 'friends', 'my_gym'] as FeedFilter[]).map((f) => (
            <TouchableOpacity
              key={f}
              style={[
                styles.filterButton,
                {
                  backgroundColor:
                    filter === f ? theme.colors.accent : theme.colors.surfaceVariant,
                  borderColor: filter === f ? theme.colors.accent : theme.colors.border,
                },
              ]}
              onPress={() => setFilter(f)}>
              <Text
                style={[
                  styles.filterButtonText,
                  {
                    color: filter === f ? theme.colors.text : theme.colors.textSecondary,
                  },
                ]}>
                {f === 'all' ? 'All' : f === 'friends' ? 'Friends' : 'My Gym'}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </LinearGradient>

      <FlatList
        data={posts}
        renderItem={renderPost}
        keyExtractor={(item) => item.id}
        onViewableItemsChanged={onViewableItemsChanged.current}
        viewabilityConfig={viewabilityConfig}
        contentContainerStyle={styles.listContent}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            tintColor={theme.colors.accent}
          />
        }
        ListHeaderComponent={
          filter === 'all' ? (
            <View style={styles.widgetsContainer}>
              <FriendStreakWidget />
              <GymStreakWidget />
            </View>
          ) : null
        }
        ListEmptyComponent={
          <EmptyState
            icon={Activity}
            title="No Posts Yet"
            description={
              filter === 'friends'
                ? "No connections yet. Follow other members to see their activity."
                : filter === 'my_gym'
                ? "No activity at your gym yet. Log a workout to appear here."
                : "No activity yet. Log a workout to begin."
            }
            actionLabel={filter === 'friends' ? 'Find Friends' : 'Log Workout'}
            onAction={() => {
              if (filter === 'friends') {
                router.push('/(tabs)/friends');
              } else {
                router.push('/(tabs)/record');
              }
            }}
            secondaryActionLabel={filter === 'all' ? 'View Leaderboards' : undefined}
            onSecondaryAction={
              filter === 'all' ? () => router.push('/(tabs)/rank') : undefined
            }
          />
        }
      />

      <TouchableOpacity
        style={[styles.fab, { backgroundColor: theme.colors.accent }]}
        onPress={() => setShowCreatePost(true)}>
        <Plus size={28} color={theme.colors.text} strokeWidth={3} />
      </TouchableOpacity>

      <CommentsModal
        visible={commentPostId !== null}
        postId={commentPostId ?? ''}
        postAuthorName={commentPostAuthor}
        onClose={() => { setCommentPostId(null); setCommentPostAuthor(undefined); }}
        onCommentAdded={() => loadFeed()}
      />
      <CreatePostModal
        visible={showCreatePost}
        onClose={() => {
          setShowCreatePost(false);
          setVideoUri(null);
          setPostDraftId(null);
          setDraftContent('');
        }}
        onPostCreated={() => {
          loadFeed();
          setVideoUri(null);
          setPostDraftId(null);
          setDraftContent('');
        }}
        videoUri={videoUri ?? undefined}
        postDraftId={postDraftId ?? undefined}
        initialContent={draftContent}
      />

      <Modal
        visible={showReasonModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => {
          setShowReasonModal(false);
          setPendingVote(null);
        }}>
        <View style={styles.modalOverlay}>
          <View style={[styles.reasonModal, { backgroundColor: theme.colors.surface }]}>
            <Text style={[styles.reasonModalTitle, { color: theme.colors.text }]}>
              Why no lift?
            </Text>
            <Text style={[styles.reasonModalSubtitle, { color: theme.colors.textSecondary }]}>
              Select the primary reason for voting no lift
            </Text>

            <ScrollView style={styles.reasonsList}>
              {[
                { value: 'DEPTH', label: "Didn't hit depth" },
                { value: 'LOCKOUT', label: 'No lockout / soft lockout' },
                { value: 'FORM_BREAKDOWN', label: 'Major form breakdown' },
                { value: 'RANGE_OF_MOTION', label: 'Insufficient ROM' },
                { value: 'PAUSE_STANDARD', label: 'Missed pause / command' },
                { value: 'CONTROL', label: 'Not controlled / excessive bounce' },
                { value: 'OTHER', label: 'Other' },
              ].map((reason) => (
                <TouchableOpacity
                  key={reason.value}
                  style={[styles.reasonOption, { borderColor: theme.colors.border }]}
                  onPress={() => submitBadVoteWithReason(reason.value)}>
                  <Text style={[styles.reasonOptionText, { color: theme.colors.text }]}>
                    {reason.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            <TouchableOpacity
              style={[styles.reasonCancelButton, { backgroundColor: theme.colors.surfaceVariant }]}
              onPress={() => {
                setShowReasonModal(false);
                setPendingVote(null);
              }}>
              <Text style={[styles.reasonCancelText, { color: theme.colors.textSecondary }]}>
                Cancel
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <DailyMotivation />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
  },
  loadingText: {
    fontSize: 14,
    fontWeight: '500',
    marginTop: 8,
  },
  header: {
    paddingTop: 60,
    paddingBottom: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    marginBottom: 16,
  },
  logoText: {
    fontSize: 28,
    fontWeight: '900',
    letterSpacing: 3,
  },
  filterContainer: {
    flexDirection: 'row',
    gap: 8,
  },
  filterButton: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 0,
    borderWidth: 1,
    alignItems: 'center',
  },
  filterButtonText: {
    fontSize: 14,
    fontWeight: '700',
  },
  listContent: {
    padding: 16,
  },
  postCard: {
    borderRadius: 2,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
  },
  postHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    fontSize: 16,
    fontWeight: '700',
  },
  postHeaderInfo: {
    marginLeft: 12,
    flex: 1,
  },
  displayName: {
    fontSize: 16,
    fontWeight: '700',
  },
  username: {
    fontSize: 14,
  },
  gymInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 2,
  },
  gymName: {
    fontSize: 12,
  },
  postMeta: {
    alignItems: 'flex-end',
    gap: 4,
  },
  timestamp: {
    fontSize: 12,
  },
  prBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderWidth: 1,
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 4,
  },
  prBadgeText: {
    fontSize: 10,
    fontWeight: '700',
  },
  competitionBadge: {
    borderWidth: 1,
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 4,
  },
  competitionBadgeText: {
    fontSize: 10,
    fontWeight: '700',
  },
  mediaContainer: {
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 12,
  },
  video: {
    width: '100%',
    height: 300,
  },
  image: {
    width: '100%',
    height: 300,
  },
  videoPlaceholder: {
    width: '100%',
    height: 300,
    justifyContent: 'center',
    alignItems: 'center',
  },
  videoPlaceholderText: {
    fontSize: 14,
  },
  postContent: {
    fontSize: 15,
    lineHeight: 22,
    marginBottom: 12,
  },
  liftInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: 2,
    marginBottom: 12,
  },
  exerciseName: {
    fontSize: 15,
    fontWeight: '700',
    flex: 1,
  },
  liftStats: {
    fontSize: 14,
    fontWeight: '600',
  },
  verificationSection: {
    borderRadius: 2,
    padding: 12,
    marginTop: 12,
    borderWidth: 1,
  },
  verificationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  verificationLabel: {
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  verificationTimeRemaining: {
    fontSize: 11,
    fontWeight: '600',
    marginTop: 2,
  },
  verificationCounts: {
    flexDirection: 'row',
    gap: 12,
  },
  verificationCount: {
    fontSize: 14,
    fontWeight: '600',
  },
  votingClosedText: {
    fontSize: 13,
    fontWeight: '600',
    textAlign: 'center',
    paddingVertical: 8,
  },
  verificationButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  verificationButton: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 0,
    alignItems: 'center',
    borderWidth: 1,
  },
  verificationButtonGood: {
  },
  verificationButtonBad: {
  },
  verificationButtonActive: {
    opacity: 0.6,
  },
  verificationButtonText: {
    fontSize: 14,
    fontWeight: '700',
  },
  verificationVerdict: {
    alignItems: 'center',
    gap: 12,
  },
  verdictMessage: {
    fontSize: 15,
    fontWeight: '700',
    textAlign: 'center',
    lineHeight: 24,
    paddingHorizontal: 8,
  },
  verdictCounts: {
    flexDirection: 'row',
    gap: 16,
    marginTop: 4,
  },
  verdictCount: {
    fontSize: 14,
    fontWeight: '600',
  },
  postActions: {
    flexDirection: 'row',
    gap: 24,
    paddingTop: 12,
    borderTopWidth: 1,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  actionText: {
    fontSize: 14,
    fontWeight: '600',
  },
  actionTextLiked: {
  },
  flexEmoji: {
    fontSize: 18,
    opacity: 0.5,
  },
  flexEmojiActive: {
    opacity: 1,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '700',
  },
  emptySubtext: {
    fontSize: 14,
    marginTop: 8,
  },
  fab: {
    position: 'absolute',
    right: 20,
    bottom: 90,
    width: 60,
    height: 60,
    borderRadius: 0,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  widgetsContainer: {
    padding: 16,
    gap: 12,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'flex-end',
  },
  reasonModal: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 24,
    maxHeight: '80%',
  },
  reasonModalTitle: {
    fontSize: 24,
    fontWeight: '800',
    marginBottom: 8,
  },
  reasonModalSubtitle: {
    fontSize: 14,
    marginBottom: 24,
  },
  reasonsList: {
    maxHeight: 400,
  },
  reasonOption: {
    padding: 16,
    borderRadius: 2,
    borderWidth: 1,
    marginBottom: 12,
  },
  reasonOptionText: {
    fontSize: 16,
    fontWeight: '600',
  },
  reasonCancelButton: {
    marginTop: 16,
    padding: 16,
    borderRadius: 0,
    alignItems: 'center',
  },
  reasonCancelText: {
    fontSize: 16,
    fontWeight: '700',
  },
  reasonBreakdown: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
  },
  reasonBreakdownTitle: {
    fontSize: 12,
    fontWeight: '700',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  reasonBreakdownText: {
    fontSize: 14,
    fontWeight: '600',
  },
});
